function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH23_PART15: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 237. Internal Helpdesk Bot ───────────────────────────────────────

  "Internal Helpdesk Bot": {
    nodes: [
      trigger("Slack webhook: New IT support request posted", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when an employee posts a message in the #it-helpdesk Slack channel or uses the /helpdesk slash command, sending the user ID, message text, channel, and timestamp.",
        setup:
          "Create a Slack app with the message.channels event subscription for your #it-helpdesk channel. Alternatively, configure a slash command /helpdesk that triggers this webhook.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify request type and determine self-service eligibility",
        280,
        "You are an IT helpdesk triage specialist. Classify the employee request into one of: password_reset, software_install, hardware_issue, vpn_access, permission_request, network_issue, other. Determine if the request can be resolved with self-service instructions. Return request_type, summary, employee_name, is_self_service, self_service_steps, priority (low/medium/high).",
        "Classify this IT helpdesk request from Slack: {webhook_payload}",
        [
          { field: "request_type", as: "request_type", type: "string" },
          { field: "summary", as: "request_summary", type: "string" },
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "is_self_service", as: "is_self_service", type: "string" },
          {
            field: "self_service_steps",
            as: "self_service_steps",
            type: "string",
          },
          { field: "priority", as: "priority", type: "string" },
        ],
      ),
      n("3", "condition", "Check: Can be resolved with self-service?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "{is_self_service}", operator: "==", value: "true" },
          ],
        },
      }),
      aiNode(
        "4",
        "OpenAI: Generate step-by-step self-service resolution guide",
        760,
        "You are an IT support knowledge base writer. Given the request type and self-service steps, generate a clear, formatted Slack message with numbered steps the employee can follow to resolve their issue independently. Include links to relevant internal docs where applicable. Return formatted_guide, doc_links.",
        "Generate a self-service guide for this IT request type: {request_type}. Steps outline: {self_service_steps}",
        [
          { field: "formatted_guide", as: "formatted_guide", type: "string" },
          { field: "doc_links", as: "doc_links", type: "string" },
        ],
      ),
      n(
        "5",
        "hitApi",
        "Jira: Create IT support ticket for non-self-service issues",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"{it_project_key}"},"summary":"{request_summary}","description":"{request_type}: {request_summary} — reported by {employee_name}","issuetype":{"name":"Service Request"},"priority":{"name":"{priority}"}}',
              },
            ],
            variables: [
              { name: "id", customName: "jira_ticket_id", type: "string" },
              { name: "key", customName: "jira_ticket_key", type: "string" },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Send resolution guide or ticket confirmation to employee",
        1240,
        "Hi {employee_name}! Re: your {request_type} request.\n\n{formatted_guide}\n\nIf this was escalated, your ticket is {jira_ticket_key}. An IT team member will follow up shortly.",
      ),
      emailNode(
        "7",
        "Email: Send ticket confirmation with reference number",
        1480,
        "{employee_email}",
        "IT Helpdesk: Your request has been received — {jira_ticket_key}",
        "Hi {employee_name},\n\nYour IT support request has been logged.\n\nTicket: {jira_ticket_key}\nType: {request_type}\nPriority: {priority}\nSummary: {request_summary}\n\nOur IT team will respond according to your priority level. You can track status in Jira or reply to this email.\n\nIT Helpdesk",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log helpdesk request and resolution path",
        1720,
        "HelpdeskLog",
        '[["{employee_name}","{request_type}","{priority}","{is_self_service}","{jira_ticket_key}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "6"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Receives IT support requests from Slack, uses AI to classify the request type and determine self-service eligibility, provides automated resolution guides for simple issues or creates Jira tickets for complex ones, notifies the employee via Slack and email, and logs all requests to Google Sheets for tracking.",
  },

  // ─── 238. IT Ticket Auto-Categorizer ──────────────────────────────────

  "IT Ticket Auto-Categorizer": {
    nodes: [
      trigger("Jira webhook: New IT issue created", "webhook_app", {
        source: "jira",
        event: "jira:issue_created",
        howItStarts:
          "Fires when a new issue is created in the IT service project in Jira, sending the issue key, summary, description, reporter, and any initial labels.",
        setup:
          "Configure a Jira webhook for the jira:issue_created event filtered to your IT project key. Ensure the webhook sends full issue details including description.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify ticket category, sub-category, and priority",
        280,
        "You are an IT ticket classification expert. Analyze the ticket summary and description to determine: category (hardware, software, network, security, access, other), sub_category (specific issue type), recommended_priority (P1-P4), confidence score (0-100), and suggested_assignee_team. Return category, sub_category, recommended_priority, confidence, suggested_team, classification_reasoning.",
        "Classify this IT support ticket — Summary: {issue_summary}, Description: {issue_description}",
        [
          { field: "category", as: "category", type: "string" },
          { field: "sub_category", as: "sub_category", type: "string" },
          {
            field: "recommended_priority",
            as: "recommended_priority",
            type: "string",
          },
          { field: "confidence", as: "confidence", type: "number" },
          { field: "suggested_team", as: "suggested_team", type: "string" },
          {
            field: "classification_reasoning",
            as: "classification_reasoning",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Validate classification confidence threshold", 520, {
        codeDetails: {
          code: "const confidence = parseFloat('{confidence}'); const autoApply = confidence >= 85; const priority = '{recommended_priority}'; return { auto_apply: autoApply, confidence_tier: confidence >= 85 ? 'high' : confidence >= 60 ? 'medium' : 'low' };",
        },
      }),
      n("4", "condition", "Check: AI confidence >= 85%?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{confidence}", operator: ">=", value: "85" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Jira: Auto-apply category labels and priority to ticket",
        1000,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue/{issue_key}",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"labels":["{category}","{sub_category}"],"priority":{"name":"{recommended_priority}"},"customfield_10001":"{suggested_team}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_issue_id", type: "string" },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Suggest classification to agent for low-confidence tickets",
        1240,
        "New IT ticket {issue_key} needs manual review.\n\nAI Suggestion (confidence: {confidence}%):\n- Category: {category} > {sub_category}\n- Priority: {recommended_priority}\n- Team: {suggested_team}\n- Reasoning: {classification_reasoning}\n\nPlease review and apply or correct.",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Track classification accuracy metrics",
        1480,
        "ClassificationAccuracy",
        '[["{issue_key}","{category}","{sub_category}","{recommended_priority}","{confidence}","{confidence_tier}","{timestamp}"]]',
      ),
      n(
        "8",
        "runCode",
        "Compute running accuracy stats for model tuning",
        1720,
        {
          codeDetails: {
            code: "const confidence = parseFloat('{confidence}'); const autoApplied = confidence >= 85; return { auto_applied: autoApplied, needs_review: !autoApplied, feedback_loop: confidence < 60 ? 'retrain_candidate' : 'acceptable' };",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Receives new Jira IT tickets, uses AI to classify category, sub-category, and priority with a confidence score, auto-applies labels for high-confidence classifications (>=85%), routes low-confidence tickets to Slack for manual agent review, and tracks classification accuracy in Google Sheets for continuous model improvement.",
  },

  // ─── 239. Ticket to Slack Alert ───────────────────────────────────────

  "Ticket to Slack Alert": {
    nodes: [
      trigger("Jira webhook: Issue created or updated", "webhook_app", {
        source: "jira",
        event: "jira:issue_updated",
        howItStarts:
          "Fires when a Jira issue in the IT project is created or has a status/priority change, sending the issue key, summary, status, priority, assignee, and changelog.",
        setup:
          "Configure a Jira webhook for jira:issue_created and jira:issue_updated events filtered to your IT project. Include changelog in the webhook payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract key details and format alert message",
        280,
        "You are an IT operations communicator. Extract the issue key, summary, current status, priority, assignee, reporter, and what changed from the Jira webhook. Determine the appropriate Slack channel based on category. Format a concise alert. Return issue_key, summary, status, priority, assignee, reporter, change_description, target_channel, alert_message.",
        "Extract and format this Jira ticket update for Slack notification: {webhook_payload}",
        [
          { field: "issue_key", as: "issue_key", type: "string" },
          { field: "summary", as: "summary", type: "string" },
          { field: "status", as: "status", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "assignee", as: "assignee", type: "string" },
          {
            field: "change_description",
            as: "change_description",
            type: "string",
          },
          { field: "target_channel", as: "target_channel", type: "string" },
        ],
      ),
      n("3", "runCode", "Determine notification routing by priority", 520, {
        codeDetails: {
          code: "const priority = '{priority}'.toLowerCase(); const isHighPriority = priority === 'p1' || priority === 'p2' || priority === 'highest' || priority === 'high'; return { is_high_priority: isHighPriority, urgency: isHighPriority ? 'immediate' : 'digest' };",
        },
      }),
      n("4", "condition", "Check: Priority is P1 or P2 (High/Highest)?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_high_priority}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Send immediate high-priority alert to #it-urgent",
        1000,
        "URGENT {priority} — {issue_key}: {summary}\n\nAssignee: {assignee}\nStatus: {status}\nChange: {change_description}\n\nJira: https://{jira_domain}.atlassian.net/browse/{issue_key}",
      ),
      n(
        "6",
        "delayAction",
        "Wait 30 minutes to batch low-priority updates",
        1240,
        { delayDetails: { duration: 30, unit: "minutes" } },
      ),
      slackNode(
        "7",
        "Slack: Post batched digest of lower-priority ticket updates",
        1480,
        "IT Ticket Update — {issue_key}: {summary}\nPriority: {priority} | Status: {status} | Assignee: {assignee}\nChange: {change_description}",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log all ticket alert events",
        1720,
        "TicketAlerts",
        '[["{issue_key}","{priority}","{status}","{assignee}","{change_description}","{urgency}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors Jira IT project for issue creates and updates, uses AI to extract details and determine routing, sends immediate Slack alerts for P1/P2 high-priority issues, batches lower-priority updates with a 30-minute digest window, and logs all alert events to Google Sheets for audit.",
  },

  // ─── 240. SLA Breach Internal Escalation ──────────────────────────────

  "SLA Breach Internal Escalation": {
    nodes: [
      trigger("Schedule: Check for SLA breaches every 30 minutes", "schedule", {
        schedule: "Every 30 minutes",
        howItStarts:
          "Runs every 30 minutes to query Jira for IT tickets approaching or exceeding their SLA response and resolution targets, fetching ticket details and time-in-status data.",
        setup:
          "Configure the schedule to run every 30 minutes during business hours. Ensure your Jira project has SLA fields configured (e.g., Time to First Response, Time to Resolution).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Jira: Fetch tickets approaching or past SLA deadlines",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/search",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "jql",
                value:
                  "project = {it_project_key} AND status != Done AND (cf[10020] <= 60m OR cf[10021] <= 120m)",
              },
              {
                key: "fields",
                value:
                  '["summary","priority","assignee","status","created","cf[10020]","cf[10021]"]',
              },
            ],
            variables: [
              {
                name: "issues",
                customName: "breaching_tickets",
                type: "object",
              },
              { name: "total", customName: "breach_count", type: "number" },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Classify breach severity and compute time remaining",
        520,
        {
          codeDetails: {
            code: "const count = parseInt('{breach_count}'); const hasCriticalBreach = count > 0; return { has_breaches: count > 0, severe_breach: count >= 3, breach_count: count, escalation_level: count >= 5 ? 'executive' : count >= 3 ? 'director' : 'manager' };",
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: More than 2 hours past SLA on any ticket?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{severe_breach}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Escalate severe breaches to IT leadership channel",
        1000,
        "SLA BREACH ESCALATION ({escalation_level} level)\n\n{breach_count} tickets are breaching SLA targets:\n{breaching_tickets}\n\nImmediate action required. Review and reassign at: https://{jira_domain}.atlassian.net/issues/?jql=project%3D{it_project_key}%20AND%20sla%20breached",
      ),
      emailNode(
        "6",
        "Email: Notify VP of Engineering about SLA breach pattern",
        1240,
        "{vp_email}",
        "URGENT: {breach_count} IT tickets breaching SLA targets",
        "This is an automated escalation alert.\n\n{breach_count} IT support tickets have exceeded their SLA targets. Escalation level: {escalation_level}.\n\nPlease review the Jira dashboard for details and ensure resources are allocated to resolve the backlog.\n\nDashboard: https://{jira_domain}.atlassian.net/issues/?jql=project%3D{it_project_key}",
      ),
      n(
        "7",
        "hitApi",
        "Jira: Bump priority on longest-breaching tickets",
        1480,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue/{longest_breach_key}",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"priority":{"name":"Highest"},"labels":["sla-breach","auto-escalated"]}',
              },
            ],
            variables: [
              { name: "id", customName: "escalated_issue_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log SLA breach patterns for trend analysis",
        1720,
        "SLABreaches",
        '[["{breach_count}","{escalation_level}","{severe_breach}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Polls Jira every 30 minutes for IT tickets approaching or exceeding SLA deadlines, classifies breach severity, escalates severe breaches via Slack and email to leadership, auto-bumps priority on longest-breaching tickets, and logs breach patterns to Google Sheets for trend analysis.",
  },

  // ─── 241. Multi-Channel IT Request Aggregator ─────────────────────────

  "Multi-Channel IT Request Aggregator": {
    nodes: [
      trigger("Webhook: IT request received from any channel", "webhook_app", {
        source: "multi-channel",
        event: "it_request.received",
        howItStarts:
          "Fires when an IT support request arrives from any channel — email, Slack, web form, or chat widget — sending the raw message content, source channel, requester info, and timestamp.",
        setup:
          "Configure webhook endpoints for each intake channel (Slack app, email forwarding rule, web form, chat widget) to send requests to a unified webhook URL with a channel identifier.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Normalize request across all channels and extract details",
        280,
        "You are an IT request intake processor. Normalize the incoming request regardless of source channel. Extract requester_name, requester_email, source_channel, issue_summary, issue_category, urgency_level (low/medium/high/critical), and a fingerprint hash of the core issue for dedup matching. Return requester_name, requester_email, source_channel, issue_summary, issue_category, urgency_level, issue_fingerprint.",
        "Normalize this IT request from {source_channel}: {webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "source_channel", as: "source_channel", type: "string" },
          { field: "issue_summary", as: "issue_summary", type: "string" },
          { field: "issue_category", as: "issue_category", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "issue_fingerprint",
            as: "issue_fingerprint",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Deduplicate against recent requests by user and fingerprint",
        520,
        {
          codeDetails: {
            code: "const fingerprint = '{issue_fingerprint}'; const email = '{requester_email}'; const isDuplicate = false; /* Would check against recent request cache */ return { is_duplicate: isDuplicate, dedup_key: `${email}_${fingerprint}` };",
          },
        },
      ),
      n("4", "condition", "Check: Is this a duplicate request?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_duplicate}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Jira: Merge with existing ticket if duplicate found",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue/{existing_ticket_key}/comment",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "body",
                value:
                  '{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Duplicate request received from {source_channel} by {requester_name}: {issue_summary}"}]}]}',
              },
            ],
            variables: [
              { name: "id", customName: "comment_id", type: "string" },
            ],
          },
        },
      ),
      n("6", "hitApi", "Jira: Create new IT ticket for unique requests", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{it_project_key}"},"summary":"{issue_summary}","description":"{issue_category}: {issue_summary} — from {requester_name} via {source_channel}","issuetype":{"name":"Service Request"},"priority":{"name":"{urgency_level}"},"labels":["{source_channel}","{issue_category}"]}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Confirm ticket creation to requester",
        1480,
        "Your IT request has been received and logged as {jira_ticket_key}.\n\nSummary: {issue_summary}\nCategory: {issue_category}\nUrgency: {urgency_level}\nSource: {source_channel}\n\nOur IT team will respond based on priority.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log aggregated request for channel analytics",
        1720,
        "ITRequestAggregator",
        '[["{requester_name}","{source_channel}","{issue_category}","{urgency_level}","{is_duplicate}","{jira_ticket_key}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Aggregates IT support requests from email, Slack, forms, and chat into a unified intake, uses AI to normalize and deduplicate requests, merges duplicates into existing Jira tickets, creates new tickets for unique issues, confirms via Slack, and logs channel analytics to Google Sheets.",
  },

  // ─── 242. Endpoint Patch Compliance Digest ────────────────────────────

  "Endpoint Patch Compliance Digest": {
    nodes: [
      trigger("Schedule: Weekly endpoint patch compliance check", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs every Monday morning to query Intune/Jamf for all managed device patch statuses, collecting OS version, last update date, compliance state, and device owner details.",
        setup:
          "Configure Microsoft Intune or Jamf Pro API credentials. Ensure all managed devices are enrolled and reporting patch status. Set your compliance threshold (default 95%).",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Intune/Jamf: Fetch managed device patch compliance status",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://graph.microsoft.com/v1.0/deviceManagement/managedDevices?$select=deviceName,osVersion,complianceState,lastSyncDateTime,userPrincipalName,operatingSystem&$filter=complianceState eq 'noncompliant'",
            headers: [
              { key: "Authorization", value: "Bearer {intune_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "value",
                customName: "noncompliant_devices",
                type: "object",
              },
              {
                name: "@odata.count",
                customName: "noncompliant_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute compliance rate and identify overdue devices",
        520,
        {
          codeDetails: {
            code: "const noncompliant = parseInt('{noncompliant_count}'); const totalDevices = parseInt('{total_managed_devices}') || 500; const complianceRate = ((totalDevices - noncompliant) / totalDevices * 100).toFixed(1); const belowThreshold = parseFloat(complianceRate) < 95; return { compliance_rate: complianceRate, below_threshold: belowThreshold, noncompliant_count: noncompliant };",
          },
        },
      ),
      n("4", "condition", "Check: Compliance rate below 95% threshold?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{below_threshold}", operator: "==", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Alert IT security about compliance gap",
        1000,
        "PATCH COMPLIANCE ALERT\n\nCurrent compliance rate: {compliance_rate}% (target: 95%)\nNon-compliant devices: {noncompliant_count}\n\nTop non-compliant devices:\n{noncompliant_devices}\n\nImmediate remediation action required.",
      ),
      emailNode(
        "6",
        "Email: Notify non-compliant device owners with update instructions",
        1240,
        "{owner_email}",
        "Action Required: Your device requires a security patch update",
        "Hi {device_owner},\n\nYour device ({device_name}) is currently non-compliant with our patch management policy.\n\nCurrent OS: {os_version}\nLast sync: {last_sync}\nRequired action: Install pending security updates\n\nPlease update your device within 48 hours. Go to Settings > Update & Security > Windows Update (or System Preferences > Software Update on Mac).\n\nIf you need assistance, contact IT at #it-helpdesk.\n\nIT Security Team",
      ),
      sheetsNode(
        "7",
        "Google Sheets: Update compliance dashboard with weekly metrics",
        1480,
        "PatchCompliance",
        '[["{compliance_rate}","{noncompliant_count}","{total_managed_devices}","{timestamp}"]]',
      ),
      n(
        "8",
        "runCode",
        "Schedule forced remediation for critically overdue devices",
        1720,
        {
          codeDetails: {
            code: "const noncompliant = parseInt('{noncompliant_count}'); const needsForceUpdate = noncompliant > 20; return { force_remediation: needsForceUpdate, remediation_scope: needsForceUpdate ? 'batch_force_update' : 'individual_notify', next_check: 'Wednesday' };",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs weekly Intune/Jamf compliance checks, computes patch compliance rates, alerts IT security via Slack when compliance drops below 95%, emails non-compliant device owners with remediation instructions, updates the compliance dashboard in Google Sheets, and schedules forced updates for critically overdue fleets.",
  },

  // ─── 243. Backup Failure Alert ────────────────────────────────────────

  "Backup Failure Alert": {
    nodes: [
      trigger("Webhook: Backup job failure detected", "webhook_app", {
        source: "backup_system",
        event: "backup.failed",
        howItStarts:
          "Fires when a scheduled backup job fails, sending the backup job ID, target system, failure reason, last successful backup timestamp, data classification level, and affected database or service name.",
        setup:
          "Configure your backup orchestration tool (Veeam, AWS Backup, or custom scripts) to send failure webhooks to this endpoint. Include data classification in job metadata.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze failure reason and determine severity",
        280,
        "You are a backup operations analyst. Analyze the backup failure details to determine: failure_category (disk_space, network, permission, corruption, timeout, unknown), severity (critical/high/medium/low) based on data classification and time since last success, recommended_action, and estimated_recovery_time. Return system_name, failure_reason, failure_category, severity, data_classification, last_success, recommended_action, recovery_estimate.",
        "Analyze this backup failure event and determine severity: {webhook_payload}",
        [
          { field: "system_name", as: "system_name", type: "string" },
          { field: "failure_reason", as: "failure_reason", type: "string" },
          { field: "failure_category", as: "failure_category", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          {
            field: "data_classification",
            as: "data_classification",
            type: "string",
          },
          { field: "last_success", as: "last_success", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "runCode",
        "Classify criticality based on data sensitivity and age",
        520,
        {
          codeDetails: {
            code: "const severity = '{severity}'; const classification = '{data_classification}'; const isCritical = severity === 'critical' || classification === 'confidential' || classification === 'restricted'; return { is_critical: isCritical, page_oncall: isCritical, notification_level: isCritical ? 'page' : 'alert' };",
          },
        },
      ),
      n("4", "condition", "Check: Critical data or extended failure?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "{is_critical}", operator: "==", value: "true" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "PagerDuty: Page on-call engineer for critical backup failure",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://events.pagerduty.com/v2/enqueue",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "routing_key", value: "{pagerduty_integration_key}" },
              { key: "event_action", value: "trigger" },
              {
                key: "payload",
                value:
                  '{"summary":"CRITICAL Backup Failure: {system_name} — {failure_reason}","severity":"critical","source":"{system_name}","component":"backup","custom_details":{"failure_category":"{failure_category}","data_classification":"{data_classification}","last_success":"{last_success}","recommended_action":"{recommended_action}"}}',
              },
            ],
            variables: [
              {
                name: "dedup_key",
                customName: "pagerduty_incident_key",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Alert infrastructure team about backup failure",
        1240,
        "BACKUP FAILURE — {severity}\n\nSystem: {system_name}\nReason: {failure_reason}\nCategory: {failure_category}\nData classification: {data_classification}\nLast successful backup: {last_success}\n\nRecommended action: {recommended_action}",
      ),
      n(
        "7",
        "hitApi",
        "Jira: Create incident ticket for backup failure investigation",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"{infra_project_key}"},"summary":"Backup failure: {system_name} — {failure_category}","description":"System: {system_name}\\nFailure: {failure_reason}\\nCategory: {failure_category}\\nSeverity: {severity}\\nData classification: {data_classification}\\nLast success: {last_success}\\nRecommended action: {recommended_action}","issuetype":{"name":"Incident"},"priority":{"name":"Highest"}}',
              },
            ],
            variables: [
              { name: "key", customName: "jira_incident_key", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log backup failure for compliance audit",
        1720,
        "BackupFailures",
        '[["{system_name}","{failure_category}","{severity}","{data_classification}","{last_success}","{jira_incident_key}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Detects backup job failures, uses AI to analyze failure reasons and classify severity based on data sensitivity, pages on-call via PagerDuty for critical data failures, alerts the infrastructure team on Slack, creates Jira incident tickets, and logs all failures to Google Sheets for compliance auditing.",
  },

  // ─── 244. Database Backup to Cloud Archive ────────────────────────────

  "Database Backup to Cloud Archive": {
    nodes: [
      trigger("Schedule: Daily database backup at 2 AM", "schedule", {
        schedule: "Every day at 2:00 AM",
        howItStarts:
          "Runs daily at 2 AM to initiate database backup procedures, creating compressed snapshots of production databases and uploading them to cloud archive storage with encryption.",
        setup:
          "Configure database connection credentials and AWS S3 bucket for backup archives. Ensure the backup service account has read access to databases and write access to S3. Set up lifecycle policies on the S3 bucket.",
        inputFields: [],
      }),
      n("2", "runCode", "Execute database backup and compress snapshot", 280, {
        codeDetails: {
          code: "const timestamp = new Date().toISOString().replace(/[:.]/g, '-'); const backupFile = `db-backup-${timestamp}.sql.gz`; const dbSize = '2.4GB'; return { backup_file: backupFile, db_size: dbSize, backup_timestamp: timestamp, compression_ratio: '4.2x' };",
        },
      }),
      n(
        "3",
        "hitApi",
        "AWS S3: Upload encrypted backup to archive bucket",
        520,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://{backup_bucket}.s3.amazonaws.com/backups/{backup_file}",
            headers: [
              {
                key: "Authorization",
                value: "AWS4-HMAC-SHA256 {aws_signature}",
              },
              { key: "Content-Type", value: "application/gzip" },
              { key: "x-amz-server-side-encryption", value: "aws:kms" },
              { key: "x-amz-storage-class", value: "STANDARD_IA" },
            ],
            body: [],
            variables: [
              { name: "ETag", customName: "s3_etag", type: "string" },
              {
                name: "x-amz-version-id",
                customName: "s3_version_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "runCode",
        "Verify backup integrity via checksum comparison",
        760,
        {
          codeDetails: {
            code: "const localChecksum = '{local_md5}'; const s3Etag = '{s3_etag}'.replace(/\"/g, ''); const checksumMatch = localChecksum === s3Etag; return { checksum_match: checksumMatch, verification_status: checksumMatch ? 'passed' : 'failed', local_checksum: localChecksum, remote_checksum: s3Etag };",
          },
        },
      ),
      n("5", "condition", "Check: Backup verification passed?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "{checksum_match}", operator: "==", value: "true" },
          ],
        },
      }),
      sheetsNode(
        "6",
        "Google Sheets: Log successful backup with metadata",
        1240,
        "BackupLog",
        '[["{backup_file}","{db_size}","{compression_ratio}","{s3_version_id}","{verification_status}","{backup_timestamp}"]]',
      ),
      slackNode(
        "7",
        "Slack: Confirm successful backup to #infrastructure",
        1480,
        "Daily DB Backup Complete\n\nFile: {backup_file}\nSize: {db_size} (compressed {compression_ratio})\nS3 version: {s3_version_id}\nChecksum: {verification_status}\nTimestamp: {backup_timestamp}",
      ),
      n(
        "8",
        "hitApi",
        "PagerDuty: Alert on backup verification failure",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://events.pagerduty.com/v2/enqueue",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "routing_key", value: "{pagerduty_integration_key}" },
              { key: "event_action", value: "trigger" },
              {
                key: "payload",
                value:
                  '{"summary":"BACKUP VERIFICATION FAILED: {backup_file} — checksum mismatch","severity":"critical","source":"backup-workflow","custom_details":{"local_checksum":"{local_checksum}","remote_checksum":"{remote_checksum}","db_size":"{db_size}"}}',
              },
            ],
            variables: [
              { name: "dedup_key", customName: "pd_dedup_key", type: "string" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Executes nightly database backups at 2 AM, compresses and uploads to AWS S3 with KMS encryption, verifies integrity via checksum comparison, logs successful backups to Google Sheets, confirms on Slack, and pages on-call via PagerDuty if verification fails.",
  },

  // ─── 245. API Uptime Alert + Escalation ───────────────────────────────

  "API Uptime Alert + Escalation": {
    nodes: [
      trigger(
        "Schedule: Check API endpoint health every 5 minutes",
        "schedule",
        {
          schedule: "Every 5 minutes",
          howItStarts:
            "Runs every 5 minutes to perform HTTP health checks against critical API endpoints, measuring response time, status code, and response body validity across all monitored services.",
          setup:
            "Configure the list of API endpoints to monitor in your environment variables. Each endpoint should have a URL, expected status code, timeout threshold, and optional response body check.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HTTP: Test primary API endpoints for availability",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "{monitored_endpoint_url}/health",
            headers: [
              { key: "Authorization", value: "Bearer {monitoring_api_key}" },
              { key: "User-Agent", value: "OmniEngage-Monitor/1.0" },
            ],
            body: [],
            variables: [
              { name: "status", customName: "http_status", type: "number" },
              {
                name: "response_time_ms",
                customName: "response_time",
                type: "number",
              },
              {
                name: "body.healthy",
                customName: "is_healthy",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute availability score and detect degradation",
        520,
        {
          codeDetails: {
            code: "const status = parseInt('{http_status}'); const responseTime = parseInt('{response_time}'); const isDown = status >= 500 || status === 0; const isSlow = responseTime > 2000; const consecutiveFailures = parseInt('{prev_failures}') || 0; const newFailures = (isDown || isSlow) ? consecutiveFailures + 1 : 0; return { is_down: isDown, is_slow: isSlow, consecutive_failures: newFailures, needs_alert: newFailures >= 3, availability_pct: isDown ? '0' : '100', response_time: responseTime };",
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: 3+ consecutive failures or response > 2s?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{needs_alert}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n("5", "hitApi", "PagerDuty: Page on-call for sustained downtime", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://events.pagerduty.com/v2/enqueue",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "routing_key", value: "{pagerduty_integration_key}" },
            { key: "event_action", value: "trigger" },
            {
              key: "payload",
              value:
                '{"summary":"API DOWN: {monitored_endpoint_url} — {consecutive_failures} consecutive failures","severity":"critical","source":"api-monitor","custom_details":{"http_status":"{http_status}","response_time":"{response_time}ms","consecutive_failures":"{consecutive_failures}"}}',
            },
          ],
          variables: [
            {
              name: "dedup_key",
              customName: "pd_incident_key",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Post real-time status update to #incidents",
        1240,
        "API HEALTH ALERT\n\nEndpoint: {monitored_endpoint_url}\nStatus: {http_status}\nResponse time: {response_time}ms\nConsecutive failures: {consecutive_failures}\n\nOn-call has been paged. Incident: {pd_incident_key}",
      ),
      n(
        "7",
        "hitApi",
        "StatusPage: Update public status page component",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.statuspage.io/v1/pages/{page_id}/components/{component_id}",
            headers: [
              { key: "Authorization", value: "OAuth {statuspage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "component", value: '{"status":"major_outage"}' }],
            variables: [
              { name: "id", customName: "component_update_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log uptime check results for SLA reporting",
        1720,
        "UptimeLog",
        '[["{monitored_endpoint_url}","{http_status}","{response_time}","{availability_pct}","{consecutive_failures}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Performs 5-minute API health checks, tracks consecutive failures and response times, pages on-call engineers via PagerDuty after 3+ consecutive failures, updates Slack and public StatusPage with outage info, and logs all check results to Google Sheets for SLA reporting.",
  },

  // ─── 246. GitHub Issue from Slack ─────────────────────────────────────

  "GitHub Issue from Slack": {
    nodes: [
      trigger("Slack webhook: Bug report reaction on message", "webhook_app", {
        source: "slack",
        event: "reaction_added",
        howItStarts:
          "Fires when someone adds a :bug: emoji reaction to a Slack message, indicating a bug report. Sends the message text, channel, reacting user, original author, and thread context.",
        setup:
          "Create a Slack app with the reaction_added event subscription. Configure the trigger to filter for the :bug: emoji. Ensure the app has channels:history scope to read the original message.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract bug report details from Slack message",
        280,
        "You are a software engineering bug triage specialist. Extract from the Slack message: a concise bug title, detailed description, reproduction steps if present, affected component or feature, severity estimate (P1-P4), and the reporter name. Also determine the most relevant GitHub repository from the context. Return bug_title, description, repro_steps, affected_component, severity, reporter, target_repo.",
        "Extract bug report details from this Slack message: {message_text}. Channel: {channel_name}. Reporter: {reporter_name}",
        [
          { field: "bug_title", as: "bug_title", type: "string" },
          { field: "description", as: "bug_description", type: "string" },
          { field: "repro_steps", as: "repro_steps", type: "string" },
          {
            field: "affected_component",
            as: "affected_component",
            type: "string",
          },
          { field: "severity", as: "severity", type: "string" },
          { field: "reporter", as: "reporter", type: "string" },
          { field: "target_repo", as: "target_repo", type: "string" },
        ],
      ),
      n("3", "runCode", "Map to GitHub repo and assign labels", 520, {
        codeDetails: {
          code: "const repo = '{target_repo}'; const severity = '{severity}'; const labels = ['bug', severity.toLowerCase()]; if ('{affected_component}') labels.push('{affected_component}'); return { repo_full: `{github_org}/${repo}`, labels: labels.join(','), is_p1: severity === 'P1' };",
        },
      }),
      n("4", "hitApi", "GitHub: Create issue with extracted bug details", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.github.com/repos/{github_org}/{target_repo}/issues",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github+json" },
          ],
          body: [
            { key: "title", value: "[Bug] {bug_title}" },
            {
              key: "body",
              value:
                "## Bug Report\n\n**Reporter:** {reporter} (via Slack #{channel_name})\n**Severity:** {severity}\n**Component:** {affected_component}\n\n### Description\n{bug_description}\n\n### Reproduction Steps\n{repro_steps}\n\n---\n_Auto-created from Slack :bug: reaction_",
            },
            { key: "labels", value: '["bug","{severity}"]' },
          ],
          variables: [
            { name: "number", customName: "issue_number", type: "number" },
            { name: "html_url", customName: "issue_url", type: "string" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Reply in thread with GitHub issue link",
        1000,
        "Bug report created! {issue_url}\n\nTitle: {bug_title}\nSeverity: {severity}\nRepo: {target_repo}\nComponent: {affected_component}\n\nTrack progress on GitHub.",
      ),
      n("6", "condition", "Check: Is this a P1 critical bug?", 1240, {
        conditionDetails: {
          conditions: [{ variable: "{is_p1}", operator: "==", value: "true" }],
        },
      }),
      slackNode(
        "7",
        "Slack: Alert engineering lead about P1 bug",
        1480,
        "P1 BUG ALERT\n\n{bug_title}\n{bug_description}\n\nGitHub: {issue_url}\nReporter: {reporter}\nComponent: {affected_component}\n\nImmediate triage required.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log bug report for triage metrics",
        1720,
        "BugReports",
        '[["{bug_title}","{severity}","{affected_component}","{target_repo}","{issue_number}","{reporter}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Captures bug reports from Slack :bug: reactions, uses AI to extract structured details and determine severity, creates GitHub issues with labels and reproduction steps, replies in Slack with the issue link, escalates P1 bugs to the engineering lead, and logs all reports to Google Sheets.",
  },

  // ─── 247. Jira Issue from GitHub Events ───────────────────────────────

  "Jira Issue from GitHub Events": {
    nodes: [
      trigger("GitHub webhook: Issue or PR created/updated", "webhook_app", {
        source: "github",
        event: "issues",
        howItStarts:
          "Fires when a GitHub issue or pull request is opened or has significant updates (label changes, milestone assignment), sending the action, issue/PR details, repository, author, and labels.",
        setup:
          "Configure a GitHub webhook on your repository or organization for 'Issues' and 'Pull requests' events. Set the payload URL to this workflow endpoint with content type application/json.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract issue details and map to Jira project context",
        280,
        "You are a DevOps workflow coordinator. Extract the GitHub event type (issue or PR), title, description, author, repository name, labels, and milestone. Map the repository to the corresponding Jira project key. Determine the Jira issue type and priority. Return event_type, gh_title, gh_description, gh_author, repo_name, labels, jira_project, jira_issue_type, jira_priority, gh_url.",
        "Extract and map this GitHub event to Jira fields: {webhook_payload}",
        [
          { field: "event_type", as: "event_type", type: "string" },
          { field: "gh_title", as: "gh_title", type: "string" },
          { field: "gh_description", as: "gh_description", type: "string" },
          { field: "gh_author", as: "gh_author", type: "string" },
          { field: "repo_name", as: "repo_name", type: "string" },
          { field: "jira_project", as: "jira_project", type: "string" },
          { field: "jira_priority", as: "jira_priority", type: "string" },
          { field: "gh_url", as: "gh_url", type: "string" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Map GitHub repo to Jira project and check for existing link",
        520,
        {
          codeDetails: {
            code: "const project = '{jira_project}'; const ghUrl = '{gh_url}'; return { jira_project_key: project, search_jql: `project = ${project} AND description ~ '${ghUrl}'`, has_mapping: project !== 'unknown' };",
          },
        },
      ),
      n("4", "hitApi", "Jira: Search for existing linked issue", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/search",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "jql", value: "{search_jql}" },
            { key: "maxResults", value: "1" },
          ],
          variables: [
            { name: "total", customName: "existing_count", type: "number" },
            {
              name: "issues[0].key",
              customName: "existing_key",
              type: "string",
            },
          ],
        },
      }),
      n(
        "5",
        "condition",
        "Check: Jira issue already exists for this GitHub item?",
        1000,
        {
          conditionDetails: {
            conditions: [
              { variable: "{existing_count}", operator: ">", value: "0" },
            ],
          },
        },
      ),
      n(
        "6",
        "hitApi",
        "Jira: Update existing issue with latest GitHub changes",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue/{existing_key}/comment",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "body",
                value:
                  '{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"GitHub update: {event_type} — {gh_title} by {gh_author}. See: {gh_url}"}]}]}',
              },
            ],
            variables: [
              { name: "id", customName: "comment_id", type: "string" },
            ],
          },
        },
      ),
      n("7", "hitApi", "Jira: Create new issue linked to GitHub", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"[GitHub] {gh_title}","description":"{gh_description}\\n\\nGitHub {event_type}: {gh_url}\\nAuthor: {gh_author}\\nRepo: {repo_name}","issuetype":{"name":"Task"},"priority":{"name":"{jira_priority}"}}',
            },
          ],
          variables: [
            { name: "key", customName: "new_jira_key", type: "string" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Notify assignee about synced Jira issue",
        1720,
        "GitHub → Jira Sync\n\n{event_type}: {gh_title}\nGitHub: {gh_url}\nJira: {existing_key}{new_jira_key}\nAuthor: {gh_author}\nPriority: {jira_priority}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Syncs GitHub issues and pull requests to Jira by extracting details with AI, searching for existing linked Jira issues, updating them if found or creating new ones if not, and notifying the team on Slack about the sync status.",
  },

  // ─── 248. Workflow Backup to GitHub ───────────────────────────────────

  "Workflow Backup to GitHub": {
    nodes: [
      trigger("Schedule: Daily workflow export and backup", "schedule", {
        schedule: "Every day at 11:00 PM",
        howItStarts:
          "Runs nightly to export all automation workflows from n8n or Make, compare against the last committed version, and push changes to a GitHub repository for version control.",
        setup:
          "Configure API credentials for your workflow automation tool (n8n or Make). Set up a dedicated GitHub repository for workflow backups with appropriate branch protection rules.",
        inputFields: [],
      }),
      n("2", "hitApi", "n8n/Make: Export all workflow definitions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{n8n_host}/api/v1/workflows?active=true",
          headers: [
            { key: "X-N8N-API-KEY", value: "{n8n_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "workflow_definitions",
              type: "object",
            },
            { name: "count", customName: "workflow_count", type: "number" },
          ],
        },
      }),
      n(
        "3",
        "runCode",
        "Generate commit message from workflow diff summary",
        520,
        {
          codeDetails: {
            code: "const count = parseInt('{workflow_count}'); const date = new Date().toISOString().split('T')[0]; const message = `chore(backup): export ${count} workflows — ${date}`; const hasChanges = true; /* Compare hash against last backup */ return { commit_message: message, has_changes: hasChanges, backup_date: date, file_path: `backups/workflows-${date}.json` };",
          },
        },
      ),
      n(
        "4",
        "condition",
        "Check: Any workflow changes detected since last backup?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{has_changes}", operator: "==", value: "true" },
            ],
          },
        },
      ),
      n(
        "5",
        "hitApi",
        "GitHub: Create commit with workflow backup file",
        1000,
        {
          hitApiDetails: {
            method: "PUT",
            url: "https://api.github.com/repos/{github_org}/{backup_repo}/contents/{file_path}",
            headers: [
              { key: "Authorization", value: "Bearer {github_token}" },
              { key: "Accept", value: "application/vnd.github+json" },
            ],
            body: [
              { key: "message", value: "{commit_message}" },
              { key: "content", value: "{base64_workflow_definitions}" },
              { key: "branch", value: "main" },
            ],
            variables: [
              { name: "commit.sha", customName: "commit_sha", type: "string" },
              {
                name: "content.html_url",
                customName: "file_url",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Confirm backup committed to GitHub",
        1240,
        "Workflow Backup Complete\n\nWorkflows exported: {workflow_count}\nCommit: {commit_sha}\nFile: {file_url}\nDate: {backup_date}\n\nAll active workflows backed up to version control.",
      ),
      emailNode(
        "7",
        "Email: Send weekly backup summary digest",
        1480,
        "{ops_team_email}",
        "Weekly Workflow Backup Summary — {backup_date}",
        "Weekly Backup Report\n\nTotal active workflows: {workflow_count}\nLatest commit: {commit_sha}\nRepository: {github_org}/{backup_repo}\n\nAll workflows are version-controlled and recoverable. Review the full history at: {file_url}\n\nAutomation Ops Team",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log backup version for audit trail",
        1720,
        "WorkflowBackups",
        '[["{backup_date}","{workflow_count}","{commit_sha}","{has_changes}","{file_path}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Nightly exports all active workflows from n8n/Make, detects changes since the last backup, commits updated definitions to a GitHub repository for version control, confirms via Slack, sends weekly email digest summaries, and logs every backup version to Google Sheets.",
  },

  // ─── 249. API Endpoint Monitoring Workflow ────────────────────────────

  "API Endpoint Monitoring Workflow": {
    nodes: [
      trigger(
        "Schedule: Monitor API response times every 5 minutes",
        "schedule",
        {
          schedule: "Every 5 minutes",
          howItStarts:
            "Runs every 5 minutes to test multiple API endpoints, measuring response times, status codes, SSL certificate validity, and response payload integrity across all production services.",
          setup:
            "Define the list of API endpoints to monitor including URL, expected status, response time SLA, and expected response body pattern. Configure alert thresholds for degraded vs. down states.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HTTP: Test API endpoints and measure response metrics",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "{endpoint_url}",
            headers: [
              { key: "User-Agent", value: "OmniEngage-APIMonitor/1.0" },
              { key: "Accept", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "status_code",
                customName: "status_code",
                type: "number",
              },
              {
                name: "response_time_ms",
                customName: "response_time_ms",
                type: "number",
              },
              { name: "body", customName: "response_body", type: "string" },
            ],
          },
        },
      ),
      n("3", "runCode", "Compare response times against SLA thresholds", 520, {
        codeDetails: {
          code: "const status = parseInt('{status_code}'); const responseTime = parseInt('{response_time_ms}'); const slaThreshold = 2000; const isError = status >= 400; const isSlow = responseTime > slaThreshold; const isHealthy = !isError && !isSlow; const trend = responseTime > 1500 ? 'degrading' : 'healthy'; return { is_error: isError, is_slow: isSlow, is_healthy: isHealthy, performance_trend: trend, sla_met: isHealthy, response_grade: isHealthy ? 'A' : isSlow ? 'C' : 'F' };",
        },
      }),
      n(
        "4",
        "condition",
        "Check: Response time > 2 seconds or HTTP error?",
        760,
        {
          conditionDetails: {
            conditions: [
              { variable: "{is_healthy}", operator: "==", value: "false" },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Alert ops about degraded endpoint performance",
        1000,
        "API PERFORMANCE ALERT\n\nEndpoint: {endpoint_url}\nStatus: {status_code}\nResponse time: {response_time_ms}ms (SLA: 2000ms)\nGrade: {response_grade}\nTrend: {performance_trend}\n\nInvestigate immediately.",
      ),
      n(
        "6",
        "hitApi",
        "PagerDuty: Page on-call if endpoint is completely down",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://events.pagerduty.com/v2/enqueue",
            headers: [{ key: "Content-Type", value: "application/json" }],
            body: [
              { key: "routing_key", value: "{pagerduty_integration_key}" },
              { key: "event_action", value: "trigger" },
              {
                key: "payload",
                value:
                  '{"summary":"API endpoint error: {endpoint_url} — HTTP {status_code}, {response_time_ms}ms","severity":"{is_error}" == "true" ? "critical" : "warning","source":"api-monitor"}',
              },
            ],
            variables: [
              { name: "dedup_key", customName: "pd_dedup_key", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "7",
        "Google Sheets: Log performance metrics for trend analysis",
        1480,
        "APIPerformance",
        '[["{endpoint_url}","{status_code}","{response_time_ms}","{response_grade}","{performance_trend}","{sla_met}","{timestamp}"]]',
      ),
      n(
        "8",
        "runCode",
        "Track performance trends and flag regression patterns",
        1720,
        {
          codeDetails: {
            code: "const responseTime = parseInt('{response_time_ms}'); const trend = '{performance_trend}'; const grade = '{response_grade}'; return { avg_response: responseTime, needs_capacity_review: responseTime > 1800, trend_direction: trend, weekly_sla_target: '99.9%', monitoring_status: grade === 'A' ? 'nominal' : 'attention_needed' };",
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors API endpoints every 5 minutes for response time and status codes, grades performance against SLA thresholds, alerts ops on Slack for degraded performance, pages on-call via PagerDuty for outages, logs metrics to Google Sheets for trend analysis, and flags capacity review needs.",
  },

  // ─── 250. Log Anomaly Triage Bot ──────────────────────────────────────

  "Log Anomaly Triage Bot": {
    nodes: [
      trigger(
        "Webhook: Log anomaly detected by monitoring system",
        "webhook_app",
        {
          source: "monitoring",
          event: "anomaly.detected",
          howItStarts:
            "Fires when your log monitoring system (Datadog, ELK, CloudWatch) detects an anomaly pattern, sending the anomaly type, affected service, log samples, timestamp range, and baseline comparison data.",
          setup:
            "Configure your log monitoring tool to send anomaly alerts via webhook. Include log samples, the anomaly detection rule that triggered, baseline metrics, and the affected service identifier.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Classify anomaly type and assess impact",
        280,
        "You are a site reliability engineer specializing in log analysis. Classify the detected anomaly into: error_spike, latency_increase, traffic_anomaly, security_event, resource_exhaustion, or application_crash. Assess the impact severity, identify the root service, and check if this correlates with any known patterns. Return anomaly_type, affected_service, severity (critical/high/medium/low), impact_description, likely_cause, recommended_investigation.",
        "Analyze this log anomaly detection event: {webhook_payload}",
        [
          { field: "anomaly_type", as: "anomaly_type", type: "string" },
          { field: "affected_service", as: "affected_service", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          {
            field: "impact_description",
            as: "impact_description",
            type: "string",
          },
          { field: "likely_cause", as: "likely_cause", type: "string" },
          {
            field: "recommended_investigation",
            as: "recommended_investigation",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Correlate anomaly with recent deployments", 520, {
        codeDetails: {
          code: "const anomalyType = '{anomaly_type}'; const service = '{affected_service}'; const hasRecentDeploy = true; /* Check deploy log for service in last 2h */ return { deploy_correlation: hasRecentDeploy, correlation_type: hasRecentDeploy ? 'likely_deploy_regression' : 'organic_anomaly', rollback_candidate: hasRecentDeploy && '{severity}' === 'critical' };",
        },
      }),
      n(
        "4",
        "condition",
        "Check: Anomaly correlates with recent deployment?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                variable: "{deploy_correlation}",
                operator: "==",
                value: "true",
              },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Alert engineering with deploy correlation analysis",
        1000,
        "LOG ANOMALY — Deploy Correlation Detected\n\nType: {anomaly_type}\nService: {affected_service}\nSeverity: {severity}\nLikely cause: {likely_cause}\n\nCorrelation: Recent deployment to {affected_service} may be responsible.\nRecommended: {recommended_investigation}\n\nConsider rollback if impact persists.",
      ),
      n(
        "6",
        "hitApi",
        "Jira: Create investigation ticket with anomaly context",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
            headers: [
              { key: "Authorization", value: "Basic {jira_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "fields",
                value:
                  '{"project":{"key":"{sre_project_key}"},"summary":"Log anomaly: {anomaly_type} in {affected_service}","description":"Anomaly Type: {anomaly_type}\\nService: {affected_service}\\nSeverity: {severity}\\nImpact: {impact_description}\\nLikely Cause: {likely_cause}\\nDeploy Correlation: {deploy_correlation}\\n\\nRecommended Investigation:\\n{recommended_investigation}","issuetype":{"name":"Bug"},"priority":{"name":"{severity}"},"labels":["anomaly","{anomaly_type}","{affected_service}"]}',
              },
            ],
            variables: [
              {
                name: "key",
                customName: "jira_investigation_key",
                type: "string",
              },
            ],
          },
        },
      ),
      emailNode(
        "7",
        "Email: Send on-call summary with investigation steps",
        1480,
        "{oncall_email}",
        "Log Anomaly Alert: {anomaly_type} in {affected_service} — {severity}",
        "Anomaly Detection Summary\n\nType: {anomaly_type}\nService: {affected_service}\nSeverity: {severity}\nImpact: {impact_description}\nLikely cause: {likely_cause}\nDeploy correlation: {deploy_correlation}\n\nRecommended investigation:\n{recommended_investigation}\n\nJira ticket: {jira_investigation_key}\n\nPlease investigate and update the ticket with findings.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log anomaly event for pattern tracking",
        1720,
        "AnomalyLog",
        '[["{anomaly_type}","{affected_service}","{severity}","{deploy_correlation}","{likely_cause}","{jira_investigation_key}","{timestamp}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Receives log anomaly alerts from monitoring tools, uses AI to classify the anomaly type and assess severity, correlates with recent deployments to identify regressions, alerts engineering on Slack with rollback guidance, creates Jira investigation tickets, and logs patterns to Google Sheets.",
  },
};
